// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Method to validate the configuration.
//
//  Copyright:    © 2025 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
// region Version 1.0.0.0
//    001   12.03.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   05.02.26 Sean Flook         GMSCM-12 Changes required for V2 of the API.
// endregion Version 1.0.0.0
//
//--------------------------------------------------------------------------------------------------
// endregion header

import { ApplicationNameDto } from "../dtos/applicationNamesDto";
import MultiTenantConfigGetDto, {
  MultiTenantConfigOrganisation,
  NamedConnectionSetting,
  NamedElasticSetting,
} from "../dtos/multiTenantConfigDto";
import ConfigErrorType, {
  ConfigConnectionErrorType,
  ConfigElasticErrorType,
  ConfigOrganisationErrorType,
} from "../models/configErrorType";
import { getConfigDocumentKey } from "./ConfigNormalization";

const namesEqual = (left: string | undefined, right: string | undefined): boolean =>
  (left ?? "").localeCompare(right ?? "", undefined, { sensitivity: "accent" }) === 0;

/**
 * Method to handle the event when the user chooses to save the changes made to the current document.
 *
 * @param {(ConfigOrganisation | undefined)} data The organisation data.
 * @return {(ConfigOrganisationErrorType[] | undefined)} The validation errors.
 */
export const validateOrganisation = (
  documentKey: string,
  _organisationsData: string[] | undefined,
  data: MultiTenantConfigOrganisation | undefined
): ConfigOrganisationErrorType[] | undefined => {
  let organisationErrors: ConfigOrganisationErrorType[] = [];

  if (data) {
    if (!data.organisationName || data.organisationName.trim().length === 0) {
      organisationErrors.push({
        id: `${documentKey}|${data.organisationName}`,
        field: "organisationName",
        error: "The organisation name is required.",
      });
    }
    // Named connection/elastic checks live in validateConfig so we don't duplicate errors.
  }

  return organisationErrors.length > 0 ? organisationErrors : undefined;
};

/**
 * Method to handle the event when the user chooses to save the changes made to the current document.
 *
 * @param {string} organisationName The name of the organisation.
 * @param {(NamedConnectionSetting | undefined)} data The connection setting data.
 * @return {(ConfigConnectionErrorType[] | undefined)} The validation errors.
 */
export const validateConnectionSetting = (
  documentKey: string,
  _appName: string,
  organisationName: string,
  _applicationNamesData: ApplicationNameDto[] | undefined,
  data: NamedConnectionSetting | undefined
): ConfigConnectionErrorType[] | undefined => {
  let connectionError: ConfigConnectionErrorType[] = [];

  if (data) {
    const settingTreeId = `${documentKey}|${organisationName}|${data.name}`;
    const settingLabel = data.name?.trim() || "connection setting";

    if (!data.name || data.name.trim().length === 0) {
      connectionError.push({
        id: settingTreeId,
        organisationName: organisationName,
        field: "connectionName",
        error: "The connection name is required.",
      });
    }

    if (data.settings) {
      const server = data.settings.server;
      const database = data.settings.database;
      const username = data.settings.username;
      const password = data.settings.password;
      const port = data.settings.port;
      const isBlankStub =
        (!server || server.trim().length === 0) &&
        (!database || database.trim().length === 0) &&
        (!username || username.trim().length === 0) &&
        (!password || password.trim().length === 0) &&
        (!port || port === 0);

      // Brand-new empty stubs: one clear message that navigates to the connection form.
      if (isBlankStub) {
        connectionError.push({
          id: settingTreeId,
          organisationName: organisationName,
          field: "connectionSettings",
          error: `Open "${settingLabel}" in the tree and complete the database connection details.`,
        });
      } else {
        if (!server || server.trim().length === 0) {
          connectionError.push({
            id: settingTreeId,
            organisationName: organisationName,
            field: "connectionServer",
            error: `"${settingLabel}": the server name is required.`,
          });
        }

        if (!database || database.trim().length === 0) {
          connectionError.push({
            id: settingTreeId,
            organisationName: organisationName,
            field: "connectionDatabase",
            error: `"${settingLabel}": the database name is required.`,
          });
        }

        if (!username || username.trim().length === 0) {
          connectionError.push({
            id: settingTreeId,
            organisationName: organisationName,
            field: "connectionUsername",
            error: `"${settingLabel}": the username is required.`,
          });
        }

        if (!password || password.trim().length === 0) {
          connectionError.push({
            id: settingTreeId,
            organisationName: organisationName,
            field: "connectionPassword",
            error: `"${settingLabel}": the password is required.`,
          });
        }

        if (!port || port === 0) {
          connectionError.push({
            id: settingTreeId,
            organisationName: organisationName,
            field: "connectionPort",
            error: `"${settingLabel}": the port is required.`,
          });
        }
      }
    } else {
      connectionError.push({
        id: settingTreeId,
        organisationName: organisationName,
        field: "connectionSettings",
        error: `Open "${settingLabel}" in the tree and complete the database connection details.`,
      });
    }
  }

  return connectionError.length > 0 ? connectionError : undefined;
};

/**
 * Method to handle the event when the user chooses to save the changes made to the current document.
 *
 * @param {string} organisationName The name of the organisation.
 * @param {(NamedElasticSetting | undefined)} data The elastic setting data.
 * @return {(ConfigElasticErrorType[] | undefined)} The validation errors.
 */
export const validateElasticSetting = (
  documentKey: string,
  _appName: string,
  organisationName: string,
  _applicationNamesData: ApplicationNameDto[] | undefined,
  data: NamedElasticSetting | undefined
): ConfigElasticErrorType[] | undefined => {
  let elasticError: ConfigElasticErrorType[] = [];

  if (data) {
    const settingTreeId = `${documentKey}|${organisationName}|${data.name}`;
    const settingLabel = data.name?.trim() || "elastic setting";

    if (!data.name || data.name.trim().length === 0) {
      elasticError.push({
        id: settingTreeId,
        organisationName: organisationName,
        field: "elasticName",
        error: "The elastic name is required.",
      });
    }

    if (data.settings) {
      const alias = data.settings.alias;
      const elasticNodes = data.settings.elasticNodes ?? [];

      if (!alias || alias.trim().length === 0) {
        elasticError.push({
          id: settingTreeId,
          organisationName: organisationName,
          field: "elasticAlias",
          error: `"${settingLabel}": the alias is required.`,
        });
      }

      if (!elasticNodes || elasticNodes.length === 0) {
        elasticError.push({
          id: settingTreeId,
          organisationName: organisationName,
          field: "elasticElasticNodes",
          error: `"${settingLabel}": click here to add an elastic node (or use Add elastic node on this form).`,
        });
      }
    } else {
      elasticError.push({
        id: settingTreeId,
        organisationName: organisationName,
        field: "elasticSettings",
        error: `"${settingLabel}": complete the elastic index details on the organisation form.`,
      });
    }
  }

  return elasticError.length > 0 ? elasticError : undefined;
};

/**
 * Method to handle the event when the user chooses to save the changes made to the current document.
 *
 * @param {(ConfigDocument | undefined)} config The configuration document.
 * @return {ConfigErrorType[] | undefined} The validation errors.
 */
export const validateConfig = (
  configDocData: MultiTenantConfigGetDto[],
  applicationNamesData: ApplicationNameDto[] | undefined,
  organisationsData: string[] | undefined,
  config: MultiTenantConfigGetDto | undefined
): ConfigErrorType[] | undefined => {
  let validationErrors: ConfigErrorType[] = [];

  if (config) {
    const documentKey = getConfigDocumentKey(config);

    if (!config.name || config.name.trim().length === 0) {
      validationErrors.push({
        id: config.id,
        treeId: documentKey,
        organisationName: "",
        field: "name",
        errors: ["The configuration name is required."],
      });
    }

    if (!config.appName || config.appName.trim().length === 0) {
      validationErrors.push({
        id: config.id,
        treeId: documentKey,
        organisationName: "",
        field: "appName",
        errors: ["The application name is required."],
      });
    }

    const existingConfig: MultiTenantConfigGetDto | undefined = configDocData.find(
      (x) => x.id !== config.id && namesEqual(x.name, config.name)
    );

    if (existingConfig) {
      validationErrors.push({
        id: config.id,
        treeId: documentKey,
        organisationName: "",
        field: "name",
        errors: ["The configuration name must be unique."],
      });
    }

    if (!config.organisations || config.organisations.length === 0) {
      validationErrors.push({
        id: config.id,
        treeId: documentKey,
        organisationName: "",
        field: "name",
        errors: ["At least one organisation is required."],
      });
    } else {
      config.organisations.forEach((org) => {
        const orgErrors: ConfigOrganisationErrorType[] | undefined = validateOrganisation(
          documentKey,
          organisationsData,
          org
        );
        if (orgErrors && orgErrors.length > 0) {
          orgErrors.forEach((orgError) => {
            validationErrors.push({
              id: config.id,
              treeId: orgError.id ? orgError.id : "",
              organisationName: org.organisationName,
              field: orgError.field,
              errors: [orgError.error],
            });
          });
        }

        const duplicateOrg: MultiTenantConfigOrganisation[] | undefined = config.organisations.filter((x) =>
          namesEqual(x.organisationName, org.organisationName)
        );

        if (duplicateOrg && duplicateOrg.length > 1) {
          validationErrors.push({
            id: config.id,
            treeId: `${documentKey}|${org.organisationName}`,
            organisationName: org.organisationName,
            field: "organisationName",
            errors: ["The organisation name must be unique."],
          });
        }

        if (!org.namedConnectionSettings || org.namedConnectionSettings.length === 0) {
          validationErrors.push({
            id: config.id,
            treeId: `${documentKey}|${org.organisationName}`,
            organisationName: org.organisationName,
            field: "namedConnectionSettings",
            errors: [
              `"${org.organisationName}": click here to add a database connection setting (or use + on the organisation in the tree).`,
            ],
          });
        } else {
          org.namedConnectionSettings.forEach((connectionSetting) => {
            const connectionErrors: ConfigConnectionErrorType[] | undefined = validateConnectionSetting(
              documentKey,
              config.appName,
              org.organisationName,
              applicationNamesData,
              connectionSetting
            );

            if (connectionErrors && connectionErrors.length > 0) {
              connectionErrors.forEach((connectionError) => {
                validationErrors.push({
                  id: config.id,
                  treeId: connectionError.id,
                  organisationName: org.organisationName,
                  field: connectionError.field,
                  errors: [connectionError.error],
                });
              });
            }

            const duplicateConnection: NamedConnectionSetting[] | undefined = org.namedConnectionSettings.filter((x) =>
              namesEqual(x.name, connectionSetting.name)
            );

            if (duplicateConnection && duplicateConnection.length > 1) {
              validationErrors.push({
                id: config.id,
                treeId: `${documentKey}|${org.organisationName}|${connectionSetting.name}`,
                organisationName: org.organisationName,
                field: "namedConnectionSettings",
                errors: ["The connection setting name must be unique."],
              });
            }
          });
        }

        if (!org.namedElasticSettings || org.namedElasticSettings.length === 0) {
          validationErrors.push({
            id: config.id,
            treeId: `${documentKey}|${org.organisationName}`,
            organisationName: org.organisationName,
            field: "namedElasticSettings",
            errors: [
              `"${org.organisationName}": click here to add an elastic index setting (or use + on the organisation in the tree).`,
            ],
          });
        } else {
          org.namedElasticSettings.forEach((elasticSetting) => {
            const elasticErrors: ConfigElasticErrorType[] | undefined = validateElasticSetting(
              documentKey,
              config.appName,
              org.organisationName,
              applicationNamesData,
              elasticSetting
            );

            if (elasticErrors && elasticErrors.length > 0) {
              elasticErrors.forEach((elasticError) => {
                validationErrors.push({
                  id: config.id,
                  treeId: elasticError.id,
                  organisationName: org.organisationName,
                  field: elasticError.field,
                  errors: [elasticError.error],
                });
              });
            }

            const duplicateElastic: NamedElasticSetting[] | undefined = org.namedElasticSettings.filter((x) =>
              namesEqual(x.name, elasticSetting.name)
            );

            if (duplicateElastic && duplicateElastic.length > 1) {
              validationErrors.push({
                id: config.id,
                treeId: `${documentKey}|${org.organisationName}|${elasticSetting.name}`,
                organisationName: org.organisationName,
                field: "namedElasticSettings",
                errors: ["The elastic setting name must be unique."],
              });
            }
          });
        }
      });
    }
  }

  return validationErrors.length > 0 ? validationErrors : undefined;
};

export const validateConfigurationDocuments = (
  configDocData: MultiTenantConfigGetDto[],
  applicationNamesData: ApplicationNameDto[] | undefined,
  organisationsData: string[] | undefined
): ConfigErrorType[] | undefined => {
  let allValidationErrors: ConfigErrorType[] = [];
  configDocData.forEach((config) => {
    const configErrors: ConfigErrorType[] | undefined = validateConfig(
      configDocData,
      applicationNamesData,
      organisationsData,
      config
    );
    if (configErrors && configErrors.length > 0) {
      allValidationErrors = allValidationErrors.concat(configErrors);
    }
  });

  return allValidationErrors.length > 0 ? allValidationErrors : undefined;
};

/** Filter validation errors down to the currently selected document. */
export const filterErrorsForDocument = (
  errors: ConfigErrorType[] | undefined,
  documentKey: string | undefined
): ConfigErrorType[] | undefined => {
  if (!errors || errors.length === 0 || !documentKey) return undefined;

  const filtered = errors.filter(
    (error) => error.treeId === documentKey || error.treeId.startsWith(`${documentKey}|`) || error.id === documentKey
  );

  return filtered.length > 0 ? filtered : undefined;
};
